import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { SimpleDateRangePicker, DateRange } from '@/components/business/shared/SimpleDateRangePicker';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { BookingFilters as FilterTypes } from '@/types/businessAppTypes';
import { useTranslation } from 'react-i18next';

interface BookingFiltersProps {
  onApplyFilters: (filters: FilterTypes) => void;
  activities: string[];
}

export function BookingFilters({ onApplyFilters, activities }: BookingFiltersProps) {
  const [dateRange, setDateRange] = useState<DateRange>({ from: undefined, to: undefined });
  const [activity, setActivity] = useState<string>('all');
  const [status, setStatus] = useState<string>('all');
  const { t } = useTranslation('business', { keyPrefix: 'bookings.filters' });
  const { t: tc } = useTranslation('common');

  const handleApplyFilters = () => {
    onApplyFilters({
      dateRange: (dateRange.from && dateRange.to) ? {
        from: dateRange.from as Date,
        to: dateRange.to as Date,
      } : undefined,
      activity: activity === 'all' ? null : activity,
      status: status === 'all' ? null : status,
    });
  };

  const handleResetFilters = () => {
    setDateRange({ from: undefined, to: undefined });
    setActivity('all');
    setStatus('all');
    onApplyFilters({});
  };

  return (
    <Card className="mb-6">
      <CardContent className="p-6">
        <div className="grid grid-cols-1 lg:grid-cols-6 gap-4 items-end">
          <div className="space-y-2 lg:col-span-2">
            <label className="text-sm font-medium">{t('dateRange')}</label>
            <SimpleDateRangePicker
              value={dateRange}
              onChange={setDateRange}
              placeholder={t('selectDateRange')}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">{t('activity')}</label>
            <Select
              value={activity}
              onValueChange={setActivity}
            >
              <SelectTrigger>
                <SelectValue placeholder={t('selectActivity')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('allActivities')}</SelectItem>
                {activities.map((activityName) => (
                  <SelectItem key={activityName} value={activityName}>
                    {activityName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">{t('status')}</label>
            <Select
              value={status}
              onValueChange={setStatus}
            >
              <SelectTrigger>
                <SelectValue placeholder={t('selectStatus')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('allStatuses')}</SelectItem>
                <SelectItem value="confirmed">{tc('confirmed')}</SelectItem>
                <SelectItem value="pending">{tc('pending')}</SelectItem>
                <SelectItem value="cancelled">{tc('cancelled')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex space-x-2 lg:col-span-2 lg:justify-end">
            <Button
              variant="outline"
              onClick={handleResetFilters}
              className="flex-1 lg:flex-none"
            >
              {tc('reset')}
            </Button>
            <Button
              onClick={handleApplyFilters}
              className="flex-1 lg:flex-none"
            >
              {tc('apply')}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}